
import { defineComponent, PropType, ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import moment from 'moment'
import Nodata from '@/components/page/nodata.vue'
import { getTableColumns, TableColumn, getCourseName, getHotCasesColumns } from './courseConfig'
import { formatNumber, formatPercent, formatAmount, maskStockName, maskStockCode, isMaskEnabled } from './tableColumns'
import { getChipStateColor, getChipShapeColor, getInPoolTimeColor, formatInPoolTime } from './chipMasterUtils'
import { getFundStatusDisplay, formatMengzhuDate, getChangeColor } from './mengzhuTrendUtils'

interface PoolData {
  symbol: string
  market: string
  name: string
  [key: string]: any
}

export default defineComponent({
  name: 'PoolTable',
  components: { Nodata },
  props: {
    dataSource: {
      type: Array as PropType<PoolData[]>,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    hasMore: {
      type: Boolean,
      default: true
    },
    loadingMore: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: 'light'
    },
    courseNo: {
      type: String,
      default: 'e4408ecedc054856946546a185e8c047'
    },
    productData: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    isClick: {
      type: Number,
      default: 2  // 1: 弹窗模式(只有双击) 2: 内嵌模式(单击+双击)
    },
    defaultSortField: {
      type: String,
      default: 'pxChangRate'
    },
    defaultSortOrder: {
      type: String,
      default: 'descend'
    },
    // 盟主趋势顶级 Tab
    mengzhuMainTab: {
      type: String,
      default: 'signalPool'
    },
    // 盟主趋势：最后一个有效交易日（用于判断最高涨幅和累计涨幅的显示逻辑）
    mengzhuLastTradingDay: {
      type: String,
      default: ''
    },
    // 盟主趋势：信号池更新时间
    mengzhuUpdateTime: {
      type: String,
      default: ''
    },
    // 盟主趋势：信号类型（latest 或 history）
    mengzhuSignalType: {
      type: String,
      default: 'latest'
    }
  },
  emits: ['change', 'row-click', 'row-dblclick', 'load-more'],
  setup(props, { emit, expose }) {
    const route = useRoute()
    const tableWrapperRef = ref<HTMLElement | null>(null)
    const tableBodyRef = ref<HTMLElement | null>(null)
    const tableHeaderRef = ref<HTMLElement | null>(null)
    // 初始化时使用默认排序
    const sortField = ref<string>(props.defaultSortField)
    const sortOrder = ref<string>(props.defaultSortOrder)

    // 判断是否启用敏感信息打码
    const shouldMask = computed(() => isMaskEnabled(route.query.mcnMode as string))

    // 获取显示的股票名称（根据打码模式）
    const getDisplayName = (name: string) => {
      return shouldMask.value ? maskStockName(name) : name
    }

    // 获取显示的股票代码（根据打码模式）
    const getDisplayCode = (code: string) => {
      return shouldMask.value ? maskStockCode(code) : code
    }

    // 重置排序到默认状态
    const resetSort = () => {
      sortField.value = props.defaultSortField
      sortOrder.value = props.defaultSortOrder
    }

    // 暴露方法给父组件
    expose({ resetSort })

    // 根据 courseNo 获取列配置
    const columns = computed(() => {
      // 盟主趋势近30天热门案例使用特定的列配置
      if (isMengzhuTrend.value && props.mengzhuMainTab === 'hotCases') {
        return getHotCasesColumns(props.courseNo)
      }
      return getTableColumns(props.courseNo)
    })
    const courseName = computed(() => getCourseName(props.courseNo))

    // 盟主趋势顶级 Tab（用于模板中访问）
    const currentMengzhuMainTab = computed(() => props.mengzhuMainTab)

    // 判断是否为盟主趋势
    const isMengzhuTrend = computed(() => props.courseNo === '399a9139a99a4300a9bf004b9949eb9f')

    // 判断是否为箱体擒龙
    const isBoxDragon = computed(() => props.courseNo === '33130259066e4650bd0b7cf6b23d9488')

    // 获取对齐类名
    const getAlignClass = (align?: string) => {
      switch (align) {
        case 'left': return 'th-left'
        case 'center': return 'th-center'
        case 'right': return 'th-right'
        default: return 'th-left'
      }
    }

    // 获取固定列的 left 值
    const getFixedLeft = (currentIndex: number) => {
      let left = 0
      for (let i = 0; i < currentIndex; i++) {
        const col = columns.value[i]
        if (col.fixed === 'left') {
          left += typeof col.width === 'number' ? col.width : parseInt(col.width as string, 10) || 0
        }
      }
      return left
    }

    // 格式化单元格值
    const formatCellValue = (val: any, formatter?: string) => {
      switch (formatter) {
        case 'number': return formatNumber(val)
        case 'percent': return formatPercent(val)
        case 'amount': return formatAmount(val)
        default: return val ?? '--'
      }
    }

    // 格式化最新价：如果为0则显示--
    const formatLastPx = (lastPx: number | null | undefined) => {
      if (lastPx === null || lastPx === undefined || lastPx === 0) {
        return '--'
      }
      return formatNumber(lastPx)
    }

    // 格式化涨幅：如果最新价为0则显示--
    const formatPxChangRate = (lastPx: number | null | undefined, pxChangRate: number | null | undefined) => {
      if (lastPx === null || lastPx === undefined || lastPx === 0) {
        return '--'
      }
      return formatPercent(pxChangRate)
    }

    // 获取单元格样式类
    const getCellClass = () => {
      // 涨跌相关列不再使用CSS类，改用内联样式处理颜色
      return []
    }

    // 获取单元格样式
    const getCellStyle = (col: TableColumn, colIndex: number, record: PoolData) => {
      const style: Record<string, string> = {}
      if (col.fixed === 'left') {
        style.left = getFixedLeft(colIndex) + 'px'
      }
      // 固定颜色（从配置中读取，如金额列 #19B7E1，总量/换手列 #959EAD）
      if (col.color) {
        style.color = col.color
      }
      // 特殊列动态颜色
      if (col.key === 'macd') {
        style.color = getMacdColor(record.macd)
      }
      // 四维猎手的资金标尺使用动态颜色，盟主趋势的成交额使用配置中的固定颜色
      if (col.key === 'amount' && !isMengzhuTrend.value) {
        style.color = getAmountColor(record.amount)
      }
      // 最高涨幅（四维猎手）：>0 红色，<=0 灰色
      if (col.key === 'highRate') {
        const val = record.highRate
        if (val === null || val === undefined || val <= 0) {
          style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
        } else {
          style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
        }
      }
      // 最大涨幅（箱体擒龙）：>0 红色，<0 绿色，=0 灰色（与涨幅逻辑一致）
      if (col.key === 'highPct') {
        const val = record.highPct
        if (val === null || val === undefined || val === 0) {
          style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
        } else if (val > 0) {
          style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
        } else {
          // val < 0
          style.color = props.theme === 'dark' ? '#39e365' : '#439731'
        }
      }
      // 最新价、涨幅、入选价：根据涨幅值显示颜色
      // >0: 红色; <0: 绿色; =0: 灰色
      // 盟主趋势的入选价使用代码列颜色，不使用涨跌色
      // 箱体擒龙的入选价(selectedPrice)使用名称列颜色，和四维猎手的名称列保持一致
      if (['lastPx', 'pxChangRate', 'selectedPx', 'closePx', 'selectedPrice'].includes(col.key)) {
        // 箱体擒龙的入选价使用名称列颜色（和四维猎手的名称列一致）
        if (isBoxDragon.value && (col.key === 'closePx' || col.key === 'selectedPrice')) {
          style.color = props.theme === 'dark' ? '#DFE2EA' : '#333'
        } else {
          // 盟主趋势的入选价(buyPx)不在这里处理，使用代码列颜色
          const rate = record.pxChangRate
          if (rate === null || rate === undefined || rate === 0) {
            style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
          } else if (rate > 0) {
            style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
          } else {
            // rate < 0
            style.color = props.theme === 'dark' ? '#39e365' : '#439731'
          }
        }
      }
      // 盟主趋势最高涨幅：根据maxRate自身的值显示颜色（和四维猎手涨幅一样）
      // >0: 红色; <0: 绿色; =0: 灰色
      if (isMengzhuTrend.value && col.key === 'maxRate') {
        const rate = record.maxRate
        if (rate === null || rate === undefined || rate === 0) {
          style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
        } else if (rate > 0) {
          style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
        } else {
          // rate < 0
          style.color = props.theme === 'dark' ? '#39e365' : '#439731'
        }
      }
      // 盟主趋势累计涨幅：根据cumRate自身的值显示颜色（红涨绿跌黑平，和四维猎手最新价一致）
      // >0: 红色; <0: 绿色; =0: 灰色
      if (isMengzhuTrend.value && col.key === 'cumRate') {
        const rate = record.cumRate
        if (rate === null || rate === undefined || rate === 0) {
          style.color = props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
        } else if (rate > 0) {
          style.color = props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
        } else {
          // rate < 0
          style.color = props.theme === 'dark' ? '#39e365' : '#439731'
        }
      }
      // 盟主趋势特定列使用代码列颜色（和四维猎手的代码列一致）
      // 信号池：入选价、题材板块、离场信号时间
      // 近30天热门案例：入选价、入选日期、离场日期
      if (isMengzhuTrend.value && ['buyPx', 'plateName', 'sellTime', 'buyTime'].includes(col.key)) {
        style.color = props.theme === 'dark' ? '#DFE2EA' : '#4D4D4D'
      }
      // 箱体擒龙总量和换手列使用代码列颜色（和四维猎手的代码列一致）
      if (isBoxDragon.value && ['volume', 'turnoverRatio'].includes(col.key)) {
        style.color = props.theme === 'dark' ? '#DFE2EA' : '#4D4D4D'
      }
      return style
    }

    // 排序处理
    const handleSort = (field: string) => {
      if (sortField.value === field) {
        if (sortOrder.value === 'ascend') {
          sortOrder.value = 'descend'
        } else if (sortOrder.value === 'descend') {
          sortField.value = ''
          sortOrder.value = ''
        } else {
          sortOrder.value = 'ascend'
        }
      } else {
        sortField.value = field
        sortOrder.value = 'ascend'
      }
      emit('change', {
        pagination: null,
        sorter: sortField.value ? { field: sortField.value, order: sortOrder.value } : {}
      })
    }

    // 滚动加载处理
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement
      if (!target) return

      // 同步表头水平滚动
      if (tableHeaderRef.value) {
        tableHeaderRef.value.scrollLeft = target.scrollLeft
      }

      const { scrollTop, scrollHeight, clientHeight } = target
      if (scrollHeight - scrollTop - clientHeight < 50) {
        if (props.hasMore && !props.loadingMore && !props.loading) {
          emit('load-more')
        }
      }
    }

    // 用于区分单击和双击的定时器
    let clickTimer: ReturnType<typeof setTimeout> | null = null
    const CLICK_DELAY = 250 // 单击延迟时间，用于区分双击

    onMounted(() => { })
    onUnmounted(() => {
      // 清理定时器
      if (clickTimer) {
        clearTimeout(clickTimer)
        clickTimer = null
      }
    })

    // 单击行
    const handleRowClick = (record: PoolData) => {
      // isClick=1 弹窗模式：没有单击，直接返回
      if (props.isClick === 1) return

      // 清除之前的定时器（如果有）
      if (clickTimer) {
        clearTimeout(clickTimer)
        clickTimer = null
      }

      // isClick=2 内嵌模式：延迟执行单击，等待判断是否为双击
      clickTimer = setTimeout(() => {
        const code = record.symbol
        const market = record.market || 'sz'
        const url = `gw://funccall?funcname=OnFireStyleEvent;Command=set_focus_stock;param=<params><code>${code}</code><market_letter>${market}</market_letter></params>`
        window.open(url)
        console.log('danji ', url)
        emit('row-click', record)
        clickTimer = null
      }, CLICK_DELAY)
    }

    // 双击行
    const handleRowDblClick = (record: PoolData) => {
      // 取消单击的延迟执行
      if (clickTimer) {
        clearTimeout(clickTimer)
        clickTimer = null
      }

      setTimeout(() => {
        console.log('shuangji')
        const code = record.symbol
        const market = record.market || 'sz'
        const clickedIndex = props.dataSource.findIndex((item: PoolData) => item.symbol === record.symbol)
        const afterClicked = props.dataSource.slice(clickedIndex + 1) as PoolData[]
        const beforeClicked = props.dataSource.slice(0, clickedIndex) as PoolData[]
        const reorderedList: PoolData[] = [record].concat(afterClicked).concat(beforeClicked)
        const stockList = reorderedList.map((item: PoolData) => `${item.market || 'sz'}:${item.symbol}`).join(',')

        // 第一个调用：设置股票列表
        const url1 = `gw://funccall?funcname=SetCurStockList;list=${stockList}`
        console.log('shuangji url1:', url1)
        window.open(url1)
        // 第二个调用：打开K线图，加延时
        setTimeout(() => {
          const url2 = `gw://funccall?funcname=OnFireCommand;setcurrent=1;Command=kline;param=<params><kline_special_event>0</kline_special_event><StyleName>JSFX</StyleName><code>${code}</code><market_letter>${market}</market_letter><periodstr>DAY</periodstr><TabName>K线图</TabName><flag_container_style>K</flag_container_style></params>`
          console.log('shuangji url2:', url2)
          window.open(url2)
          // 第三个调用：显示指标，再加延时
          setTimeout(() => {
            const indAlias = (props.productData && props.productData[0]?.poolName) || courseName.value
            const url3 = `gw://funccall?funcname=OnFireCommand;Command=yin_ind_show;param=<params><ind_alias>${indAlias}</ind_alias><ingnore_paras>${1}</ingnore_paras></params>`
            console.log('shuangji url3:', url3)
            window.open(url3)
            emit('row-dblclick', record)
          }, 100)
        }, 100)
      }, 50)
    }


    // 趋势势能相关
    // 大于0：强势 黄色；小于0：弱势 绿色；等于0：-
    const getMacdColor = (val: number) => {
      if (val > 0) return '#f8a000'
      if (val < 0) return props.theme === 'dark' ? '#39e365' : '#439731'
      return props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
    }
    const getMacdText = (val: number) => {
      if (val === null || val === undefined) return '--'
      if (val === 0) return '-'
      const status = val > 0 ? '强势' : '弱势'
      return `${status}(${val.toFixed(2)})`
    }

    // 资金标尺相关
    // 大于0：强势 红色；小于0：弱势 蓝色；等于0：-
    const getAmountColor = (val: number) => {
      if (val > 0) return props.theme === 'dark' ? '#ff5c5c' : '#cc2b1d'
      if (val < 0) return props.theme === 'dark' ? '#00ffff' : '#0079d5'
      return props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d'
    }
    const getAmountText = (val: number) => {
      if (val === null || val === undefined) return '--'
      if (val === 0) return '-'
      const formattedVal = formatAmount(val)
      const status = val > 0 ? '强势' : '弱势'
      return `${status}(${formattedVal})`
    }

    // 盟主趋势成交额格式化（只显示金额，和四维猎手的成交额列一致）
    const getMengzhuAmountText = (val: number) => {
      if (val === null || val === undefined) return '--'
      if (val === 0) return '-'
      // 只显示金额，不显示强势/弱势
      return formatAmount(val)
    }

    // 题材追踪相关
    // 核心：紫色；普通：黄色；未入选：黑色
    const getPlateStyle = (type: string | number) => {
      const typeStr = String(type)
      if (typeStr === '1') return { color: '#883cd1' } // 核心 紫色
      if (typeStr === '2') return { color: '#f8a000' } // 普通 黄色
      return { color: props.theme === 'dark' ? '#c8c8c8' : '#4d4d4d' } // 未入选 黑色
    }
    const getPlateText = (type: string | number, name: string) => {
      const typeStr = String(type)
      if (typeStr === '1') return `${name}(核心)`
      if (typeStr === '2') return `${name}(普通)`
      return `未入选`
    }

    // 筹码大师相关
    // 筹码状态颜色
    const getChipStateColorStyle = (state: string, stateColor?: number) => {
      return getChipStateColor(state, stateColor, props.theme as 'light' | 'dark')
    }

    // 筹码形态颜色（固定灰色）
    const getChipShapeColorStyle = () => {
      return getChipShapeColor(props.theme as 'light' | 'dark')
    }

    // 入选时间颜色（与序号相同）
    const getInPoolTimeColorStyle = () => {
      return getInPoolTimeColor(props.theme as 'light' | 'dark')
    }

    // 格式化入选时间
    const formatInPoolTimeValue = (timestamp: number | undefined | null) => {
      return formatInPoolTime(timestamp)
    }

    // 盟主趋势相关
    // 盟主资金状态颜色
    const getFundStatusColorStyle = (fundStatus: number | undefined | null) => {
      const result = getFundStatusDisplay(fundStatus, props.theme as 'light' | 'dark')
      return result.color
    }

    // 盟主资金状态文本
    const getFundStatusText = (fundStatus: number | undefined | null) => {
      const result = getFundStatusDisplay(fundStatus, props.theme as 'light' | 'dark')
      return result.text || '--'
    }

    // 格式化盟主趋势日期
    const formatMengzhuDateValue = (timestamp: number | undefined | null) => {
      return formatMengzhuDate(timestamp)
    }

    // 判断盟主趋势最新信号模式下，0值是否应该显示为无效（"-"）
    // 逻辑：对比最后一个有效交易日和信号池的 updateTime 日期
    // 如果日期相同：后端返回的涨跌幅字段为0时，显示为"-"（无效）
    // 如果交易日大于信号池日期（+1天）：涨跌幅字段正常显示，0是有效值，显示为"0%"
    // 历史信号模式：0值始终显示为 "0.00%"（有效值）
    const shouldShowZeroAsInvalid = () => {
      // 只在盟主趋势最新信号模式下生效
      if (!isMengzhuTrend.value) return true
      if (props.mengzhuMainTab !== 'signalPool') return true
      // 历史信号模式：0值是有效值，不显示为无效
      if (props.mengzhuSignalType !== 'latest') return false

      const lastTradingDay = props.mengzhuLastTradingDay
      const updateTimeStr = props.mengzhuUpdateTime

      if (!lastTradingDay || !updateTimeStr) return true

      // 获取 updateTime 的日期部分（YYYY-MM-DD）
      const updateTimeDate = moment(updateTimeStr).format('YYYY-MM-DD')

      // 如果最后一个有效交易日和信号池日期相同，0值显示为无效
      // 如果最后一个有效交易日大于信号池日期，0值是有效值
      return lastTradingDay <= updateTimeDate
    }

    // 格式化盟主趋势最高涨幅
    // 盟主趋势最新信号模式下：
    // - 如果最后一个有效交易日和信号池日期相同：0值显示为"-"（无效）
    // - 如果最后一个有效交易日大于信号池日期：0值是有效值，显示为"0%"
    const formatMengzhuMaxRate = (val: number | undefined | null) => {
      if (val === null || val === undefined) return '-'
      if (val === 0) {
        // 判断0值是否应该显示为无效
        if (shouldShowZeroAsInvalid()) {
          return '-'
        }
        return '0.00%'
      }
      // maxRate 是小数形式（如 0.05 表示 5%），需要乘以100
      const percent = val * 100
      return percent.toFixed(2) + '%'
    }

    // 格式化盟主趋势累计涨幅
    // 信号池：需要乘以100（cumRate 是小数形式，如 0.1694 表示 16.94%）
    // 近30天热门案例：不需要乘以100（cumRate 已经是百分比形式，如 16.94 表示 16.94%）
    // 盟主趋势最新信号模式下：
    // - 如果最后一个有效交易日和信号池日期相同：0值显示为"-"（无效）
    // - 如果最后一个有效交易日大于信号池日期：0值是有效值，显示为"0%"
    const formatMengzhuCumRate = (val: number | undefined | null, mainTab?: string) => {
      if (val === null || val === undefined) return '-'
      if (val === 0) {
        // 近30天热门案例：0值始终显示为"-"
        if (mainTab === 'hotCases') {
          return '-'
        }
        // 信号池：判断0值是否应该显示为无效
        if (shouldShowZeroAsInvalid()) {
          return '-'
        }
        return '0.00%'
      }
      // 近30天热门案例不需要乘以100
      if (mainTab === 'hotCases') {
        return val.toFixed(2) + '%'
      }
      // 信号池需要乘以100
      const percent = val * 100
      return percent.toFixed(2) + '%'
    }

    // 盟主趋势涨跌幅颜色
    const getMengzhuChangeColor = (value: number | undefined | null) => {
      return getChangeColor(value, props.theme as 'light' | 'dark')
    }

    return {
      tableWrapperRef,
      tableBodyRef,
      tableHeaderRef,
      sortField,
      sortOrder,
      columns,
      courseName,
      shouldMask,
      isMengzhuTrend,
      isBoxDragon,
      currentMengzhuMainTab,
      getDisplayName,
      getDisplayCode,
      getAlignClass,
      getFixedLeft,
      formatCellValue,
      formatLastPx,
      formatPxChangRate,
      getCellClass,
      getCellStyle,
      handleSort,
      handleScroll,
      handleRowClick,
      handleRowDblClick,
      getMacdColor,
      getMacdText,
      getAmountColor,
      getAmountText,
      getMengzhuAmountText,
      getPlateStyle,
      getPlateText,
      // 筹码大师相关
      getChipStateColorStyle,
      getChipShapeColorStyle,
      getInPoolTimeColorStyle,
      formatInPoolTimeValue,
      // 盟主趋势相关
      getFundStatusColorStyle,
      getFundStatusText,
      formatMengzhuDateValue,
      formatMengzhuMaxRate,
      formatMengzhuCumRate,
      getMengzhuChangeColor
    }
  }
})
